import React, { useState, useMemo } from 'react';
import type { AdminContentItem } from '../../lib/types';

interface NicknamesSectionProps {
  nicknames: AdminContentItem[];
  onAddNickname: (nickname: string) => Promise<void>;
  onUpdateNickname: (id: string, nickname: string, isActive: boolean) => Promise<void>;
  onDeleteNickname: (id: string) => Promise<void>;
}

export const NicknamesSection: React.FC<NicknamesSectionProps> = ({
  nicknames,
  onAddNickname,
  onUpdateNickname,
  onDeleteNickname,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Add form state
  const [newNickname, setNewNickname] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Editing state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editNickname, setEditNickname] = useState('');

  const filteredNicknames = useMemo(() => {
    return nicknames.filter(n => {
      return !searchTerm || (n.nickname || '').toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [nicknames, searchTerm]);

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const trimmed = newNickname.trim();
    if (!trimmed) {
      setFormError('Nickname is required');
      return;
    }

    const isDup = nicknames.some(n => (n.nickname || '').toLowerCase() === trimmed.toLowerCase());
    if (isDup) {
      setFormError('This nickname already exists');
      return;
    }

    setIsSubmitting(true);
    try {
      await onAddNickname(trimmed);
      setNewNickname('');
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Failed to add nickname');
    } finally {
      setIsSubmitting(false);
    }
  };

  const startEdit = (item: AdminContentItem) => {
    setEditingId(item.id);
    setEditNickname(item.nickname || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const handleSaveEdit = async (id: string, isActive: boolean) => {
    const trimmed = editNickname.trim();
    if (!trimmed) return;
    try {
      await onUpdateNickname(id, trimmed, isActive);
      setEditingId(null);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to update nickname');
    }
  };

  return (
    <div className="admin-content-sub-section">
      <div className="section-toolbar">
        <form onSubmit={handleAddSubmit} className="add-item-form">
          <input
            type="text"
            placeholder="New affectionate nickname..."
            value={newNickname}
            onChange={e => setNewNickname(e.target.value)}
            disabled={isSubmitting}
            className="input-field phrase-input"
          />
          <button type="submit" disabled={isSubmitting} className="action-btn action-btn-add">
            {isSubmitting ? 'Adding...' : 'Add Nickname'}
          </button>
        </form>

        {formError && <div className="form-error-inline">{formError}</div>}

        <div className="filters-row">
          <input
            type="search"
            placeholder="Search nicknames..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="input-field search-input"
          />
        </div>
      </div>

      <div className="table-responsive">
        <table className="admin-data-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Nickname</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredNicknames.length === 0 ? (
              <tr>
                <td colSpan={4} className="empty-state">
                  No nicknames found.
                </td>
              </tr>
            ) : (
              filteredNicknames.map(item => {
                const isEditing = editingId === item.id;
                return (
                  <tr key={item.id} className={!item.isActive ? 'row-inactive' : ''}>
                    <td>
                      <button
                        type="button"
                        className={`status-pill ${item.isActive ? 'status-active' : 'status-disabled'}`}
                        onClick={() =>
                          onUpdateNickname(item.id, item.nickname || '', !item.isActive)
                        }
                        title="Toggle active status"
                      >
                        {item.isActive ? 'Active' : 'Disabled'}
                      </button>
                    </td>

                    <td className="cell-text">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editNickname}
                          onChange={e => setEditNickname(e.target.value)}
                          className="input-field input-inline"
                        />
                      ) : (
                        <span className="nickname-display">{item.nickname}</span>
                      )}
                    </td>

                    <td className="cell-meta">
                      {new Date(item.createdAt).toLocaleDateString()}
                    </td>

                    <td className="cell-actions">
                      {isEditing ? (
                        <div className="action-buttons-group">
                          <button
                            type="button"
                            className="btn-link"
                            onClick={() => handleSaveEdit(item.id, item.isActive)}
                          >
                            Save
                          </button>
                          <button type="button" className="btn-link btn-muted" onClick={cancelEdit}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="action-buttons-group">
                          <button
                            type="button"
                            className="btn-link"
                            onClick={() => startEdit(item)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn-link btn-danger"
                            onClick={() => {
                              if (confirm(`Delete nickname "${item.nickname}"?`)) {
                                onDeleteNickname(item.id);
                              }
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
